import { useContext } from 'react';
import { shallowEqual } from 'react-redux';

import { Box, Button, ButtonGroup, Heading, List, Modal, Text, TextButton } from '@zillow/constellation-10';

import { PaymentGatewayContext } from '../../PaymentGateway/context';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../../constants/FreemiumConstants';
import { useSelector } from '../../../selectors/useSelector';

import type { DatarouterError } from '@zg-rentals/rental-manager-env';

export type FreemiumDatarouterError = {
  errorDetails?: {
    listingActivationFailed?: boolean;
    activationError?: {
      message?: string;
    };
    data?: {
      listing?: {
        [key: string]: string;
      };
      floorPlans?: {
        [floorPlanId: string]: {
          [field: string]: string;
        };
      };
    };
  };
} & DatarouterError;

type Props = {
  onClose: () => void;
  onPublishForFreeClick: () => void;
  onTryAgainClick: () => void;
  error?: FreemiumDatarouterError;
};

type ValidationError = {
  key: string;
  message: string;
};

type ErrorData = NonNullable<NonNullable<FreemiumDatarouterError['errorDetails']>['data']>;

const getAllValidationErrors = (errorData?: ErrorData): Array<ValidationError> => {
  const errors: Array<ValidationError> = [];

  // Get listing errors
  if (errorData?.listing) {
    Object.entries(errorData.listing).forEach(([field, message]) => {
      if (typeof message === 'string') {
        errors.push({ key: `listing-${field}`, message });
      }
    });
  }

  // Get floor plan errors
  if (errorData?.floorPlans) {
    Object.entries(errorData.floorPlans).forEach(([floorPlanId, floorPlan]) => {
      if (floorPlan) {
        Object.entries(floorPlan).forEach(([field, message]) => {
          if (typeof message === 'string') {
            errors.push({ key: `floorPlan-${floorPlanId}-${field}`, message });
          }
        });
      }
    });
  }

  return errors;
};

const FreemiumErrorModal = ({ onClose, onPublishForFreeClick, onTryAgainClick, error }: Props) => {
  const {
    pgState: { isPremiumMidfunnelPayment },
  } = useContext(PaymentGatewayContext);

  const { selectedProductType } = useSelector((state) => {
    return {
      selectedProductType: state.properties.currentProperty.selectedProductType,
    };
  }, shallowEqual);

  const getErrorMessage = (error?: FreemiumDatarouterError) => {
    if (!error) return null;

    // Handle activation error format
    const { errorDetails, message: errorMessage } = error;
    const { listingActivationFailed, activationError } = errorDetails || {};

    // general message
    let message = errorMessage;

    if (listingActivationFailed) {
      if (activationError?.message) {
        // more specific message
        message = activationError?.message;
      }
    }

    return message;
  };

  const cancelProps: { submitText: string; onSubmit: () => void; cancelText?: string; onCancel?: () => void } = {
    submitText: 'Try again',
    onSubmit: selectedProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM ? onTryAgainClick : onPublishForFreeClick,
  };
  if (isPremiumMidfunnelPayment) {
    cancelProps.cancelText = 'Cancel and edit listing';
    cancelProps.onCancel = onClose;
  } else if (selectedProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM) {
    cancelProps.cancelText = 'Publish for free';
    cancelProps.onCancel = onPublishForFreeClick;
  }

  const validationErrors = getAllValidationErrors(error?.errorDetails?.data);

  return (
    <Modal
      data-test="FreemiumErrorModal"
      open={true}
      onOpenChange={(open) => !open && onClose()}
      size={{
        base: 'fullScreen',
        lg: 'md',
      }}
      header={<Heading level={1}>Error</Heading>}
      body={
        <>
          <Text asChild>
            <p>
              {getErrorMessage(error) ||
                "An error occurred with your payment method and your premium upgrade for this listing couldn't be processed. Please retry or use another payment method to upgrade to premium. Or, if you prefer, you can publish your listing for free now and upgrade to premium later."}
            </p>
          </Text>
          {validationErrors.length > 0 && (
            <Box css={{ marginTop: '400' }}>
              <Text asChild>
                <p>Details:</p>
              </Text>
              <List.Root>
                {validationErrors.map(({ key, message }) => (
                  <List.Item key={key}>{message}</List.Item>
                ))}
              </List.Root>
            </Box>
          )}
        </>
      }
      footer={
        <ButtonGroup aria-label="modal actions">
          <TextButton onClick={cancelProps?.onCancel}>{cancelProps?.cancelText}</TextButton>
          <Button emphasis="filled" tone="brand" onClick={cancelProps.onSubmit}>
            {cancelProps.submitText}
          </Button>
        </ButtonGroup>
      }
    ></Modal>
  );
};

export default FreemiumErrorModal;
